import { useState } from 'react';
import Button from 'react-bootstrap/Button';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import Form from 'react-bootstrap/Form';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import appConfig from '../../../app-config';
import { cognitoAuthenticateUser } from '../../../authentication/cognito';
import { ButtonType, ButtonVariant, FeedbackType, FormCheckType, FormControlType, PasswordPeeker, Variant } from '../..';
import * as formik from 'formik';
import * as yup from 'yup';

import '../login.scss';

export default function FormLogin({ onNotifyEvent, onSwitchToValidateEmail, onSwitchToCreateAccount, onLoginClicked }) {

    const [submitButtonEnabled, setSubmitButtonEnabled] = useState(true);
    const [passwordVisible, setPasswordVisible] = useState(false);

    const handleSwitchToValidateEmail = () => {
        onSwitchToValidateEmail();
    }

    const handleSwitchToCreateAccount = () => {
        onSwitchToCreateAccount();
    }

    const handleLoginClick = (e) => {
        setSubmitButtonEnabled(false);

        cognitoAuthenticateUser(e.email, e.password, e.rememberMe).then((result) => {
            onLoginClicked();

            onNotifyEvent({
                email: e.email,
                notificationTitle: 'Logged In',
                notificationMessage: result,
                notificationType: Variant.SUCCESS,
            });
        }).catch((error) => {
            setSubmitButtonEnabled(true);

            onNotifyEvent({
                notificationTitle: 'Login Error',
                notificationMessage: error,
                notificationType: Variant.DANGER,
            });
        });
    }

    const onPasswordPeekerChanged = (e) => {
        setPasswordVisible(e.passwordVisible);
    }

    const { Formik } = formik;

    const schema = yup.object().shape({
        email: yup.string().matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+.[A-Z]{2,}$/i, 'Invalid email format').required('Email address is required'),
        password: yup.string().required('Password is required'),
    });

    const initialValues = {
        email: '',
        password: '',
        rememberMe: false,
    }

    return (
        <div>
            <div className={'app-login-header'}>
                <div className={'app-title'}>{appConfig.app.title}</div>
                <div>{'Sign in to your account'}</div>
            </div>

            <Formik
                validationSchema={schema}
                initialValues={initialValues}
                onSubmit={handleLoginClick}
            >
                {({ handleSubmit, handleChange, values, touched, errors }) => (
                    <Form noValidate onSubmit={handleSubmit}>

                        <Row>
                            <Form.Group as={Col} size='sm'>
                                <FloatingLabel
                                    label='Email address'
                                    className='mb-3'
                                >
                                    <Form.Control
                                        type={FormControlType.EMAIL}
                                        name='email'
                                        placeholder='name@example.com'
                                        value={values.email}
                                        onChange={handleChange}
                                        isValid={touched.email && !errors.email}
                                        isInvalid={touched.email && !!errors.email}
                                    />
                                    <Form.Control.Feedback></Form.Control.Feedback>
                                    <Form.Control.Feedback type={FeedbackType.INVALID}>{errors.email}</Form.Control.Feedback>
                                </FloatingLabel>
                            </Form.Group>
                        </Row>

                        <Row>
                            <Form.Group as={Col} size='sm'>
                                <FloatingLabel
                                    label='Password'
                                    className='mb-3'
                                >
                                    <Form.Control
                                        type={passwordVisible ? FormControlType.TEXT : FormControlType.PASSWORD}
                                        className='app-password-input-box'
                                        name='password'
                                        placeholder='Password'
                                        value={values.password}
                                        onChange={handleChange}
                                        isValid={touched.email && !errors.password}
                                        isInvalid={touched.email && !!errors.password}
                                    />
                                    <PasswordPeeker onChange={onPasswordPeekerChanged} passwordVisible={passwordVisible} />
                                    <Form.Control.Feedback></Form.Control.Feedback>
                                    <Form.Control.Feedback type={FeedbackType.INVALID}>{errors.password}</Form.Control.Feedback>
                                </FloatingLabel>
                            </Form.Group>
                        </Row>

                        <Row>
                            <Form.Group as={Col} size='sm'>
                                <Form.Check
                                    type={FormCheckType.SWITCH}
                                    label={'Remember me'}
                                    value={values.rememberMe}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </Row>

                        <Row className='app-form-button-row'>
                            <Button
                                variant={ButtonVariant.PRIMARY}
                                type={ButtonType.SUBMIT}
                                disabled={!submitButtonEnabled}
                            >
                                Login
                            </Button>
                        </Row>

                        <Row className='app-form-button-link-row'>
                            <Button
                                className='app-button-link'
                                variant={ButtonVariant.LINK}
                                onClick={handleSwitchToValidateEmail}
                            >
                                Forgot password?
                            </Button>
                        </Row>

                        {(appConfig.app.enableUserCreation === true &&
                            <>
                                <Row className='app-form-button-row'>
                                    <Button
                                        variant={ButtonVariant.SUCCESS}
                                        type={ButtonType.SUBMIT}
                                        onClick={handleSwitchToCreateAccount}
                                        disabled={!submitButtonEnabled}
                                    >
                                        Create an account
                                    </Button>
                                </Row>
                            </>
                        )}
                    </Form>
                )}

            </Formik>
        </div>
    );
}